import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getBatchProgress } from 'src/redux/api/inventory/imageProcessingBatches';
import BatchOperationToasts, {
  BatchOperationItem,
} from 'src/screens/shared/components/BatchOperationToasts';

import BulkEnhanceConfirmModal from './BulkEnhanceConfirmModal';
import { useBulkEnhancement } from './BulkEnhancementContext';
import BulkEnhanceReviewModal from './BulkEnhanceReviewModal';
import { getVehicleDisplayInfo, isReviewableBatch } from './helpers';

const BulkEnhancementManager = () => {
  const { t } = useTranslation();
  const { openBatches, openReviewBatch, reviewBatch, cancelBatch } = useBulkEnhancement();
  void t; // useTranslation kept for future use

  const visibleBatches = useMemo(
    () =>
      openBatches.filter(
        (batch) =>
          batch.id !== reviewBatch?.id &&
          (batch.status === 'processing' || isReviewableBatch(batch))
      ),
    [openBatches, reviewBatch?.id]
  );

  const batchItems: BatchOperationItem[] = useMemo(
    () =>
      visibleBatches.map((batch) => {
        const { title, thumbnail } = getVehicleDisplayInfo(batch);
        const { completed, total } = getBatchProgress(batch);
        const isProcessing = batch.status === 'processing';

        return {
          id: batch.id,
          title,
          thumbnail,
          progress: isProcessing ? { current: completed, total } : undefined,
          completedStatus: !isProcessing ? { status: 'completed' } : undefined,
          isProcessing,
          buttons: {
            cancel: isProcessing
              ? {
                  show: true,
                  onClick: () => cancelBatch(batch),
                }
              : undefined,
            review: !isProcessing
              ? {
                  show: true,
                  onClick: () => openReviewBatch(batch),
                }
              : undefined,
          },
        };
      }),
    [visibleBatches, cancelBatch, openReviewBatch]
  );

  const summaryText = t('vehicleScreen:bulk_enhance_multiple_processes', {
    count: visibleBatches.length,
  });

  return (
    <>
      <BatchOperationToasts batches={batchItems} summaryText={summaryText} />
      <BulkEnhanceConfirmModal />
      <BulkEnhanceReviewModal />
    </>
  );
};

export default BulkEnhancementManager;
